// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import urlonStringify from '@/utils/urlon';

import { RootState } from '../index';

const DEBUG = false;

type FetchConfigProps = {
  id: string;
};

type FetchConfigResult = {
  data: {
    data: any[];
  };
  success: boolean;
};

type FetchConfigError = {
  rejectValue: {
    error: string;
  };
};

const apiUrl = window.config.dataSourceApiUrl;

export const fetchWheelsDataSource = createAsyncThunk<FetchConfigResult, FetchConfigProps, FetchConfigError>(
  'wheelsDataSource/list',
  async ({ id }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      if (!state.authentication.access_token) {
        return rejectWithValue({
          error: 'No access token found in state',
        });
      }

      const response = await axios.get(`${apiUrl}/resolve/sources`, {
        headers: {
          Authorization: 'Bearer ' + state.authentication.access_token,
        },
        params: {
          q: urlonStringify({
            ids: [id],
          }),
        },
      });

      //console.log('fetchWheelsDataSource', response.data);

      if (response.data?.[id]) {
        return { data: response.data?.[id], success: true };
      }

      return rejectWithValue({
        error: "Couldn't fetch wheels data source",
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  },
);

export interface ConfigReducer {
  wheels: any;
  loading: boolean;
  loaded: boolean;
  error: any;
}

export const wheelsDataSourceSlice = createSlice({
  name: 'wheelsDataSource',
  initialState: <ConfigReducer>{
    wheels: {},
    loading: false,
    loaded: false,
    error: null,
  },
  reducers: {
    clearWheelsDataSource: (state) => {
      state.wheels = {};
      state.loaded = false;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWheelsDataSource.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.loaded = true;

          if (action.payload.data && action.payload.data.data) {
            const data: any = action.payload.data.data;
            const wheels: any = {};

            data?.forEach?.((item: any) => {
              if (item?.id) {
                wheels[item?.id] = item;
              }
            });

            state.wheels = wheels;
          }
        }
        DEBUG && console.log('fetchWheelsDataSource.fulfilled', action.payload);
      })
      .addCase(fetchWheelsDataSource.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWheelsDataSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('fetchWheelsDataSource.rejected', action.payload);
      });
  },
});

export const { clearWheelsDataSource } = wheelsDataSourceSlice.actions;

export default wheelsDataSourceSlice.reducer;
